import { baseApi } from './baseApi';

export interface BankNetbankingInfo {
  bankId: string;
  lumpsum: boolean;
  hybrid: boolean;
}

export interface BankEmandateInfo {
  bankId: string;
  netBanking: boolean;
  debitCard: boolean;
  aadhaar: boolean;
  pan: boolean;
}

export interface BankDetailsItem {
  id: number | string;
  name: string;
  ifscCode: string;
  netbanking: BankNetbankingInfo;
  emandate: BankEmandateInfo;
}

export interface CreateBankDetailsRequest {
  gateway: string;
  bankId: string;
  ifsc: string;
  bankName: string;
  remarks: string;
  isActive: boolean;
  lumpsumNetBanking: boolean;
  hybridNetBanking: boolean;
  emandateNetBanking: boolean;
  emandateDebitCard: boolean;
  emandateAadhar: boolean;
  emandatePAN: boolean;
  otmNetBanking: boolean;
  otmDebitCard: boolean;
}

export interface UpdateBankDetailsRequest extends CreateBankDetailsRequest {
  id: string;
}

export interface GetBankDetailsParams {
  gateway?: string;
  ifsc?: string;
  bankName?: string;
  merchantId?: string;
  page?: number; // 1-based per spec
  limit?: number;
}

export interface BankDetailsMeta {
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

export interface BankDetailsResponse {
  data: BankDetailsItem[];
  meta?: BankDetailsMeta;
}

export interface BankHistoryParams {
  ifsc?: string;
  gateway?: string;
}

export interface BankHistoryChange {
  field: string;
  from: unknown;
  to: unknown;
}

export interface BankHistoryItem {
  id?: string;
  // viewHistory returns two event types now:
  //   - BANK_DETAIL: row-level changes on the bank itself (isActive, bankId,
  //     bankid_corporate, bankName).
  //   - SETTLEMENT_DAYS: per-merchant settlement-day overrides on
  //     `kfin_pay_merchant_bank`. These also carry a `merchantId`.
  // Blacklist activity has its own dedicated endpoint and UI surface.
  type?: 'BANK_DETAIL' | 'SETTLEMENT_DAYS';
  transactionType?: string;
  paymode?: string;
  gateway?: string;
  ifsc?: string;
  identifier?: string;
  // Set only on SETTLEMENT_DAYS events (the merchant whose override changed).
  merchantId?: string;
  // The new viewHistory shape uses `eventDate`; older clients also looked at
  // `updatedDate`, so we keep both optional for safety.
  eventDate?: string;
  updatedDate?: string;
  remarks?: string | null;
  updatedBy?: string;
  // SETTLEMENT_DAYS rows can be CREATE (mapping first set) or UPDATE.
  action?: 'UPDATE' | 'CREATE';
  changes?: BankHistoryChange[];
  // Legacy field retained for any callers still reading it
  bankId?: string;
}

export interface BankHistoryResponse {
  success: boolean;
  data: {
    data: BankHistoryItem[];
    meta?: BankDetailsMeta;
  };
}

export interface DownloadExcelRequest {
  gateway: string;
  ifsc?: string;
  bankName?: string;
}

export interface Gateway {
  id: string;
  name: string;
  displayName: string;
  isActive: boolean;
}

export interface GatewaysResponse {
  success: boolean;
  gateways: string[];
}

export interface BlacklistEntryRequest {
  id?: number | string;
  merchantId: string;
  bankName: string;
  ifsc: string;
  gateway: string;
  isActive: boolean;
  reason?: string;
  paymode?: string;
  trtype?: string;
}

export interface BlacklistBulkUploadResponse {
  success: boolean;
  message: string;
  data: {
    processed: number;
    inserted: number;
    errors: string[];
  };
}

export interface SettlementDaysBulkUploadResponse {
  success: boolean;
  message: string;
  data: {
    processed: number;
    inserted: number;
    errors: string[];
  };
}

export interface BlacklistAuditLog {
  id: number;
  merchantId: string;
  gateway: string;
  ifsc?: string | null;
  trtype?: string | null;
  paymode?: string | null;
  isActive: boolean;
  reason?: string | null;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  performedBy?: string | null;
  createdAt: string;
}

export interface GetBlacklistAuditLogsParams {
  merchantId?: string;
  gateway?: string;
  ifsc?: string;
  page?: number;
  limit?: number;
}

export interface BlacklistAuditLogsResponse {
  success: boolean;
  data: BlacklistAuditLog[];
  meta?: BankDetailsMeta;
}

const removeNullOrUndefined = (obj: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== '')
  );

export const bankApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBankDetails: builder.query<BankDetailsResponse, GetBankDetailsParams | void>({
      query: (params) => ({
        url: '/bank-details',
        params: params ? removeNullOrUndefined(params as Record<string, any>) : undefined,
      }),
      providesTags: ['BankDetails'],
    }),

    createBankDetails: builder.mutation<BankDetailsItem, CreateBankDetailsRequest>({
      query: (bankData) => ({
        url: '/bank-details',
        method: 'POST',
        body: bankData,
      }),
      invalidatesTags: ['BankDetails'],
    }),
    downloadExcel: builder.mutation<Blob, DownloadExcelRequest>({
      query: (params) => ({
        url: '/bank-details/download-excel',
        method: 'GET',
        params,
        responseHandler: (response) => response.blob(),
      }),
      invalidatesTags: ['BankDetails'],
    }),
    updateBankDetails: builder.mutation<BankDetailsItem, UpdateBankDetailsRequest>({
      query: (bankData) => ({
        url: '/bank-details',
        method: 'PUT',
        body: bankData,
      }),
      invalidatesTags: ['BankDetails'],
    }),

    getBankDetailsHistory: builder.query<BankHistoryResponse, BankHistoryParams>({
      query: (params) => ({
        url: '/bank-details/history',
        params: params ? removeNullOrUndefined(params) : undefined,
      }),
      providesTags: ['BankDetails'],
    }),

    getGateways: builder.query<GatewaysResponse, void>({
      query: () => ({
        url: '/dashboard/gateways',
      }),
    }),

    blacklistBulkUpload: builder.mutation<BlacklistBulkUploadResponse, FormData>({
      query: (formData) => ({
        url: '/bank-details/blacklist/bulk-upload',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['BankDetails'],
    }),

    bulkUploadSettlementDays: builder.mutation<SettlementDaysBulkUploadResponse, FormData>({
      query: (formData) => ({
        url: '/bank-details/bulk-upload-tsettlementdays',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['BankDetails'],
    }),

    createBlacklistEntry: builder.mutation<{ success: boolean; message: string }, BlacklistEntryRequest>({
      query: (entryData) => ({
        url: '/bank-details/blacklist',
        method: 'POST',
        body: entryData,
      }),
      invalidatesTags: ['BankDetails'],
    }),

    getBlacklistAuditLogs: builder.query<BlacklistAuditLogsResponse, GetBlacklistAuditLogsParams>({
      query: (params) => ({
        url: '/bank-details/blacklist/audit',
        params: removeNullOrUndefined(params as Record<string, any>),
      }),
      providesTags: ['BankDetails'],
    }),
  }),
});

export const {
  useGetBankDetailsQuery,
  useLazyGetBankDetailsQuery,
  useCreateBankDetailsMutation,
  useUpdateBankDetailsMutation,
  useGetBankDetailsHistoryQuery,
  useDownloadExcelMutation,
  useGetGatewaysQuery,
  useBlacklistBulkUploadMutation,
  useBulkUploadSettlementDaysMutation,
  useCreateBlacklistEntryMutation,
  useGetBlacklistAuditLogsQuery,
  useLazyGetBlacklistAuditLogsQuery,
} = bankApi;
